/**
 * Shim for page-builder CTAs (Replo) that open the theme quick-add modal.
 *
 * Visible CTAs carry data-prepro-cta or data-quick-add-cta; a hidden opener
 * from quick-add-cta-proxy.liquid lives in a fixed off-screen host. Native
 * quick-add-cta sections use modal-opener directly and do not need this file
 * except when prefetch-on-hover is enabled.
 */
(() => {
  if (window.__quickAddCtaInit) return;

  const CTA_SELECTOR = '[data-quick-add-cta], [data-prepro-cta]';
  const LOADING_CLASS = 'quick-add-cta-loading';
  const LEGACY_LOADING_CLASS = 'prepro-cta-loading';
  const LOADING_TIMEOUT_MS = 8000;
  const PREFETCH_SECTION_SELECTOR = '.quick-add-cta[data-prefetch]';
  const NATIVE_BUTTON_SELECTOR =
    `${PREFETCH_SECTION_SELECTOR} .quick-add-cta__button[data-product-url]`;

  class QuickAddCta {
    #warmedOpeners = new WeakSet();
    #hardened = new WeakSet();
    #normalizeScheduled = false;

    shouldInit() {
      return this.hasExternalCtas() || this.hasPrefetchSection();
    }

    hasExternalCtas() {
      return !!document.querySelector(CTA_SELECTOR);
    }

    hasPrefetchSection() {
      return !!document.querySelector(PREFETCH_SECTION_SELECTOR);
    }

    /**
     * Many Replo CTAs share one hidden opener. data-prepro-cta values are
     * analytics labels, not pairing keys — only data-quick-add-target overrides.
     */
    resolveOpener(cta) {
      const targetUid = cta.getAttribute('data-quick-add-target');

      if (targetUid) {
        return document.querySelector(
          `[data-quick-add-opener="${targetUid}"]`,
        );
      }

      return (
        document.querySelector('[data-quick-add-opener]') ??
        document.querySelector('.js-prepro-quickshop-opener') ??
        document.querySelector('[data-prepro-opener]')
      );
    }

    /** Resolve via the opener's modal-opener, not the first quick-add-modal on the page. */
    resolveModal(opener) {
      const modalOpener = opener?.closest('modal-opener');
      const selector = modalOpener?.getAttribute('data-modal');

      return selector ? document.querySelector(selector) : null;
    }

    /**
     * Strip Replo link semantics and override focus so keyboard users stay
     * on-page (preventScroll avoids scroll jumps from off-screen openers).
     */
    normalize(cta) {
      if (this.#hardened.has(cta)) return;
      this.#hardened.add(cta);

      cta.removeAttribute('role');

      if (cta.tagName === 'BUTTON') {
        cta.setAttribute('type', 'button');
      }

      cta.setAttribute('aria-haspopup', 'dialog');
      cta.onclick = null;
      cta.focus = () => {
        HTMLElement.prototype.focus.call(cta, { preventScroll: true });
      };
    }

    normalizeAll() {
      document.querySelectorAll(CTA_SELECTOR).forEach((cta) => {
        this.normalize(cta);
      });
    }

    /** Replo re-hydrates buttons after initial paint — debounce to one pass per frame. */
    scheduleNormalize = () => {
      if (this.#normalizeScheduled) return;

      this.#normalizeScheduled = true;
      requestAnimationFrame(() => {
        this.#normalizeScheduled = false;
        this.normalizeAll();
      });
    };

    clearLoading(cta) {
      cta.removeAttribute('aria-busy');
      cta.classList.remove(LOADING_CLASS, LEGACY_LOADING_CLASS);
    }

    /**
     * Show spinner on the visible CTA while QuickAddModal.show() fetches the PDP.
     * Reassign openedBy so focus returns to the CTA when the modal closes.
     */
    setLoading(cta, modal) {
      cta.setAttribute('aria-busy', 'true');
      cta.classList.add(LOADING_CLASS);

      if (!modal) {
        setTimeout(() => this.clearLoading(cta), LOADING_TIMEOUT_MS);
        return;
      }

      let done = false;

      const finish = () => {
        if (done) return;
        done = true;
        this.clearLoading(cta);
        observer.disconnect();
      };

      const observer = new MutationObserver(() => {
        if (!modal.hasAttribute('open')) return;

        modal.openedBy = cta;
        finish();
      });

      observer.observe(modal, {
        attributes: true,
        attributeFilter: ['open'],
      });

      setTimeout(finish, LOADING_TIMEOUT_MS);
    }

    /** Prefetch the PDP HTML once per opener — same request QuickAddModal.show() makes. */
    warmOpener(opener) {
      if (!opener || this.#warmedOpeners.has(opener)) return;

      this.#warmedOpeners.add(opener);

      const url = opener.getAttribute('data-product-url');
      if (!url) return;

      fetch(url, { credentials: 'same-origin' }).catch(() => {});
    }

    warmFromEvent = (event) => {
      const externalCta = event.target.closest(CTA_SELECTOR);

      if (externalCta) {
        this.warmOpener(this.resolveOpener(externalCta));
        return;
      }

      const nativeButton = event.target.closest(NATIVE_BUTTON_SELECTOR);
      if (nativeButton) this.warmOpener(nativeButton);
    };

    /** Capture phase runs before Replo's role="link" click handlers. */
    handleClick = (event) => {
      const cta = event.target.closest(CTA_SELECTOR);
      if (!cta) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      if (cta.hasAttribute('aria-busy')) return;

      const opener = this.resolveOpener(cta);
      if (!opener) return;

      this.setLoading(cta, this.resolveModal(opener));
      opener.click();
    };

    bindExternalCtas() {
      this.normalizeAll();

      new MutationObserver(this.scheduleNormalize).observe(document.body, {
        childList: true,
        subtree: true,
      });

      window.addEventListener('click', this.handleClick, true);
    }

    bindPrefetch() {
      window.addEventListener('pointerover', this.warmFromEvent, true);
      window.addEventListener('touchstart', this.warmFromEvent, {
        capture: true,
        passive: true,
      });
    }

    init() {
      if (this.hasExternalCtas()) {
        this.bindExternalCtas();
      }

      if (this.hasExternalCtas() || this.hasPrefetchSection()) {
        this.bindPrefetch();
      }
    }
  }

  const bootstrap = () => {
    const controller = new QuickAddCta();
    if (!controller.shouldInit()) return;

    window.__quickAddCtaInit = true;
    controller.init();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
